/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable, Icon, Image } from '@components/atoms'
import { Footer } from '@components/systems'

import { useNavigate } from 'react-router-dom'
import { useAppDispatch, paymentActions, CartType } from '@redux/index'
import { grey, pink, lime } from '@mui/material/colors'
import jsConvertCase from 'js-convert-case'
import { Decimal } from '@utils'

import kedem6Image from '@public/images/pages/shop/kedem6/thumbnail/kedem6-thumb2.jpg'
import kedem4Image from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb2.jpg'
import amp2Image from '@public/images/pages/shop/amp2/thumbnail/amp2-thumb2.jpg'
import dsImage from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery2.jpg'
import noImage from '@public/images/pages/shop/noimage.jpg'

const imageObj = {
	KDM6: kedem6Image,
	KDM4: kedem4Image,
	AMP2: amp2Image,
	DS: dsImage,
}

const CartPage = () => {
	// hook
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// state
	const [cartList, setCartList] = React.useState<CartType[]>([])

	const vatTotalUsd: number = React.useMemo(() => {
		const res = cartList.reduce(
			(sum, { sumUsd, vatUsd }) => Decimal(sum + sumUsd + vatUsd),
			0,
		)
		return res
	}, [cartList])

	React.useEffect(() => {
		getLocalCartList()
	}, [])

	const getLocalCartList = () => {
		const localeCartSrt = localStorage.getItem('cart')
		const res: CartType[] =
			localeCartSrt === null || localeCartSrt === '' ? [] : JSON.parse(localeCartSrt)

		setCartList(res)
	}

	const onQtyUpDownClick = (id: string, upDown: 'up' | 'down') => {
		const idx = cartList.findIndex(anItem => anItem.id === id)

		if (idx > -1) {
			const thisItem: CartType = { ...cartList[idx] }

			if (upDown === 'down' && thisItem.qty > 1) {
				thisItem.qty = thisItem.qty - 1
			} else if (upDown === 'up') {
				const isAddPossible =
					thisItem.seasonQuantity - thisItem.soldQuantity - thisItem.qty > 0

				if (isAddPossible) {
					thisItem.qty = thisItem.qty + 1
				}
			}

			const unitUsd: number = Decimal(cartList[idx].seasonUsd)
			const subtotalUsd = Decimal(unitUsd * thisItem.qty)
			const priceExUsd = Decimal(subtotalUsd)
			const vatUsd = Decimal(subtotalUsd - priceExUsd)

			cartList[idx].qty = thisItem.qty
			cartList[idx].sumUsd = priceExUsd
			cartList[idx].vatUsd = vatUsd

			localStorage.setItem('cart', JSON.stringify(cartList))
			window.dispatchEvent(new Event('storage'))

			getLocalCartList()
		}
	}

	const onItemDeleteClick = (id: string) => {
		const idx = cartList.findIndex(anItem => anItem.id === id)

		if (idx > -1) {
			cartList.splice(idx, 1)
			localStorage.setItem('cart', JSON.stringify(cartList))
			window.dispatchEvent(new Event('storage'))

			getLocalCartList()
		}
	}

	const onSubmitClick = () => {
		reduxDispatch(paymentActions.PAYMENT_METHOD(''))
		reduxDispatch(
			paymentActions.TRY_TO_PAYMENT({
				cartList: cartList,
			}),
		)
	}

	return (
		<View css={style}>
			<View className="outerView">
				<View className="contentOuterView">
					<Text category="h1">CART</Text>

					<View className="contentView">
						<View>
							{cartList.length === 0 ? (
								<View>
									<Text className="emptyWording" category="h2">
										Your pedalboard is calling—let’s add some magic!
									</Text>

									<Pressable
										className="goToTheShopButton"
										onClick={() => navigate('/shop')}
									>
										<View>
											<Text category="h2">Go to Shop 👉</Text>
										</View>
									</Pressable>
								</View>
							) : (
								<View className="cartListView">
									{cartList.map(anItem => {
										const shopName = jsConvertCase.toCamelCase(anItem.productName)
										const imageKey = anItem.productCode as keyof typeof imageObj
										const image = imageKey in imageObj ? imageObj[imageKey] : noImage

										return (
											<View key={anItem.id} className="cartItemView">
												<View className="itemView name">
													<Pressable
														className="itemButton"
														onClick={() => navigate(`/shop/${shopName}`)}
													>
														{image !== null && (
															<View>
																<View className="itemImageView">
																	<Image
																		src={image}
																		className="itemImage"
																		onLoad={event => {
																			const imgElement = event.target as HTMLImageElement
																			const isPortrait =
																				imgElement.naturalHeight > imgElement.naturalWidth
																			if (isPortrait) {
																				imgElement.style.width = '100%'
																				imgElement.style.height = 'auto'
																			} else {
																				imgElement.style.width = 'auto'
																				imgElement.style.height = '100%'
																			}
																		}}
																	/>
																</View>
															</View>
														)}
														<View className="itemNameView">
															<Text className="itemName" category="h5">
																{anItem.productName}
															</Text>

															{anItem.option?.id !== '' && (
																<Text hint="500">{anItem.option?.name}</Text>
															)}
														</View>
													</Pressable>
												</View>

												<View className="itemDetailOuterView">
													<View className="itemDetailView qty">
														<Pressable
															className="qtyUpDownButton"
															onClick={() => onQtyUpDownClick(anItem.id, 'down')}
														>
															<Icon name="minus-thick" className="qtyUpDownIcon" />
														</Pressable>
														<Text category="h5" className="itemQty">
															{anItem.qty}
														</Text>
														<Pressable
															className="qtyUpDownButton"
															onClick={() => onQtyUpDownClick(anItem.id, 'up')}
														>
															<Icon name="plus-thick" className="qtyUpDownIcon" />
														</Pressable>
													</View>

													<View className="itemDetailView usd">
														<Text category="h5">
															{Decimal(anItem.sumUsd + anItem.vatUsd).toLocaleString(
																'en-US',
																{
																	style: 'currency',
																	currency: 'USD',
																},
															)}
														</Text>
													</View>
													<View className="itemDetailView delete">
														<Pressable
															className="deleteButton"
															onClick={() => onItemDeleteClick(anItem.id)}
														>
															<Icon
																name="delete-forever-outline"
																className="deleteIcon"
															/>
														</Pressable>
													</View>
												</View>
											</View>
										)
									})}

									<View className="totalOuterView">
										<View className="totalView">
											<Text category="h1" className="totalLabel">
												Total:
											</Text>
											<Text category="h1" className="totalValue">
												{vatTotalUsd.toLocaleString('en-US', {
													style: 'currency',
													currency: 'USD',
												})}
											</Text>
										</View>
									</View>

									<View className="paymentView">
										<Pressable className="paymentButton" onClick={onSubmitClick}>
											<View>
												<Text category="h2">Checkout</Text>
											</View>
										</Pressable>
									</View>

									{/* <View className="eventView">
										<View className="eventInnerView">
											<Text className="eventTitle" category="h1">
												📢 Pre-Order Closing Soon!
											</Text>
											<ul className="eventUl">
												<li className="eventLi">⏰ Finish in May</li>
												<li className="eventLi">🚀 Shipping starts in July</li>
												<li className="eventLi">🚛 Free shipping – no extra cost!</li>
											</ul>

											<Text className="eventPrice">💥 Special Prices:</Text>
											<p className="eventPriceUnit">
												Kedem 6: <span className="eventNormalPrice">$407</span> ⮕{' '}
												<span className="eventDiscountedPrice">$369</span>
											</p>
											<p className="eventPriceUnit">
												Kedem 4: <span className="eventNormalPrice">$341</span> ⮕{' '}
												<span className="eventDiscountedPrice">$305</span>
											</p>
										</View>
									</View> */}
								</View>
							)}
						</View>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default CartPage

const style = css({
	position: 'relative',
	width: '100%',

	'.outerView': {
		display: 'flex',
		paddingTop: '2rem',
		paddingBottom: '2rem',
		borderBottom: '1px solid',
		borderColor: grey[300],

		'.contentOuterView': {
			width: '100%',
			minHeight: 'calc(100vh - 10rem)',
			padding: '2rem',

			'.contentView': {
				borderTop: '1px solid',
				borderColor: grey[500],

				'.emptyWording': {
					marginTop: '4rem',
					marginBottom: '4rem',
				},

				'.goToTheShopButton': {
					paddingLeft: '2rem',
					paddingRight: '2rem',
					border: '2px solid',
					borderColor: grey[900],
				},

				'.cartListView': {
					paddingLeft: '2rem',
					paddingRight: '2rem',

					'.cartItemView': {
						width: '100%',
						minHeight: '4rem',
						display: 'flex',
						borderBottom: '1px solid',
						borderColor: grey[300],

						'.itemView': {
							width: '50%',
							textAlign: 'left',

							'.itemButton': {
								display: 'flex',
								justifyContent: 'flex-start',
								alignItems: 'center',

								'.itemImageView': {
									display: 'flex',
									flex: '1 1 auto',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									overflow: 'hidden',
									width: '10rem',
									height: '8rem',

									'.itemImage': {
										width: '100%',
										heght: 'auto',
										objectFit: 'contain',
									},
								},

								'.itemNameView': {
									paddingLeft: '3rem',
									textAlign: 'left',

									'.itemName': {},
								},
							},
						},

						'.itemDetailOuterView': {
							display: 'flex',
							justifyContent: 'flex-end',
							width: '50%',

							'.itemDetailView': {
								textAlign: 'right',

								'&.qty': {
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									width: '14rem',

									'.qtyUpDownButton': {
										'.qtyUpDownIcon': {
											fontSize: '24px',
											color: grey[600],
										},
									},

									'.itemQty': {
										minWidth: '3rem',
										textAlign: 'center',
									},
								},
								'&.usd': {
									display: 'flex',
									justifyContent: 'flex-end',
									alignItems: 'center',
									width: '15rem',
									paddingRight: '2rem',
								},
								'&.delete': {
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									width: '5rem',

									'.deleteButton': {
										'.deleteIcon': {
											fontSize: '30px',
										},
									},
								},
							},
						},
					},

					'.eventView': {
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						width: '100%',
						marginTop: '2rem',

						'.eventInnerView': {
							width: '32rem',
							padding: '2rem 4rem',
							color: grey[900],
							textAlign: 'left',
							background: lime['A200'],
							borderRadius: '10px',

							'.eventTitle': {
								fontSize: '30px',
								marginTop: '0',
								marginBottom: '0',
							},

							'.eventUl': {
								'.eventLi': {
									fontSize: '20px',
									fontWeight: '800',
									marginLeft: '1rem',
									marginBottom: '0.5rem',
								},
							},

							'.eventPrice': {
								fontSize: '30px',
								marginTop: '2rem',
								marginBottom: '1rem',
							},

							'.eventPriceUnit': {
								marginLeft: '3rem',
								marginTop: '0',
								marginBottom: '0',
								fontSize: '20px',
								fontWeight: '800',

								'.eventNormalPrice': {
									marginLeft: '1rem',
									marginRight: '1rem',
									textDecoration: 'line-through',
									fontSize: '15px',
									fontWeight: '300',
								},
								'.eventDiscountedPrice': {
									marginLeft: '1rem',
									fontSize: '30px',
								},
							},
						},
					},
				},

				'.totalOuterView': {
					textAlign: 'right',
					borderBottom: '1px solid',
					'.totalView': {
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
						marginBottom: '2rem',
						textAlign: 'left',

						'.totalLabel': {
							marginRight: '1rem',
							textAlign: 'left',
							marginBottom: 0,
						},

						'.totalValue': {
							marginBottom: 0,
						},
					},

					'.vatLabel': {
						marginTop: '0.3rem',
						marginBottom: '2rem',
						fontSize: '20px',
					},
				},

				'.paymentView': {
					paddingTop: '2rem',
					textAlign: 'right',

					'.paymentButton': {
						paddingLeft: '4rem',
						paddingRight: '4rem',
						background: grey[900],
						color: 'white',
						borderRadius: '5px',
					},
				},
			},
		},
	},

	'@media (max-width: 800px)': {
		'.outerView': {
			'.contentOuterView': {
				'.contentView': {
					'.cartListView': {
						'.cartItemView': {
							paddingTop: '1rem',
							paddingBottom: '1rem',

							'.itemView': {
								'.itemButton': {
									display: 'block',

									'.itemNameView': {
										paddingLeft: 0,
									},
								},
							},
							'.itemDetailOuterView': {},
						},

						'.eventView': {
							'.eventInnerView': {
								width: '30rem',
								padding: '2rem',

								'.eventTitle': {
									fontSize: '20px',
								},

								'.eventUl': {
									'.eventLi': {
										fontSize: '15px',
									},
								},

								'.eventPrice': {
									fontSize: '20px',
								},

								'.eventPriceUnit': {
									marginLeft: '3rem',
									fontSize: '15px',

									'.eventNormalPrice': {
										marginLeft: '1rem',
										marginRight: '1rem',
										fontSize: '12px',
									},
									'.eventDiscountedPrice': {
										fontSize: '15px',
									},
								},
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 610px)': {
		'.outerView': {
			'.contentOuterView': {
				'.contentView': {
					'.cartListView': {
						'.cartItemView': {
							'.itemView': {
								'.itemButton': {},
							},
							'.itemDetailOuterView': {
								display: 'block',

								'.itemDetailView': {
									width: '100%',

									'&.qty': {
										justifyContent: 'center',
									},
									'&.usd': {
										justifyContent: 'center',
										paddingRight: 0,
									},
									'&.delete': {
										justifyContent: 'center',
										width: '100%',
										paddingTop: '2rem',
										textAlign: 'center',

										'.deleteButton': {
											width: '100%',

											'.deleteIcon': {
												color: pink['A400'],
											},
										},
									},
								},
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 500px)': {
		'.outerView': {
			'.contentOuterView': {
				'.contentView': {
					'.cartListView': {
						padding: 0,
						'.cartItemView': {
							display: 'block',
							paddingTop: '1rem',

							'.itemView': {
								width: '100%',
								textAlign: 'center',

								'.itemButton': {
									justifyContent: 'center',
									margin: 'auto',

									'.itemImageView': {
										width: '100%',
										height: '40vw',
									},

									'.itemNameView': {
										textAlign: 'center',
									},
								},
							},
							'.itemDetailOuterView': {
								display: 'block',
								width: '100%',
								paddingBottom: '3rem',

								'.itemDetailView': {
									margin: 'auto',

									'&.qty': {
										justifyContent: 'center',
									},
									'&.usd': {
										justifyContent: 'center',
									},
									'&.delete': {
										justifyContent: 'center',
										paddingTop: '2rem',
									},
								},
							},
						},

						'.totalOuterView': {
							textAlign: 'center',

							'.totalView': {
								justifyContent: 'center',
							},
						},

						'.paymentView': {
							textAlign: 'center',
						},

						'.eventView': {
							width: '100%',
							'.eventInnerView': {
								width: '100%',
								padding: '1rem',

								'.eventTitle': {
									fontSize: '20px',
								},

								'.eventUl': {
									'.eventLi': {
										marginLeft: '0rem',
										fontSize: '15px',
									},
								},

								'.eventPrice': {
									fontSize: '20px',
								},

								'.eventPriceUnit': {
									marginLeft: '2rem',
									fontSize: '15px',

									'.eventNormalPrice': {
										marginLeft: '1rem',
										marginRight: '1rem',
										fontSize: '12px',
									},
									'.eventDiscountedPrice': {
										fontSize: '15px',
									},
								},
							},
						},
					},
				},
			},
		},
	},
})
